import React, { useState, useEffect, useRef } from "react";
import MaterialTable from "@material-table/core";
import axiosImport from "axios";
import axios from "../AxiosClient";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import InputLabel from "@material-ui/core/InputLabel";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Skeleton from "@material-ui/lab/Skeleton";
import CardContent from "@material-ui/core/CardContent";
import Card from "@material-ui/core/Card";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import FormHelperText from "@material-ui/core/FormHelperText";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import TaxonomySelector from "../components/taxonomy/TaxonomySelector";
import { Check, Error } from "@material-ui/icons";
import { Link } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { useLocation } from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Alert from "@material-ui/lab/Alert";
import moment from "moment";
import DocumentType from "../components/DocumentType";
import ChipInput from "material-ui-chip-input";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { useSelector } from "react-redux";
import DocumentCategories from "../components/document/DocumentCategories";
import { languages } from "../components/search/SearchLanguages";
import WarningIcon from "@material-ui/icons/Warning";

const useStyles = makeStyles((theme) => ({
  detailContainer: {
    display: "flex",
    backgroundColor: theme.palette.grey[300],
  },
  table: {
    margin: 10,
    width: "20%",
  },
  settingsTable: {
    margin: 10,
    flex: 1,
  },
  indexableWords: {
    margin: 10,
    flex: 1,
  },
  docLanguage: {
    margin: "10px 0",
  },
}));

function TaxonomyFilter(props) {
  const [topic, setTopic] = useState([]);
  useEffect(
    () => {
      let topics = [];
      topic.forEach((topic) => {
        if (topic.type === "topic") {
          topics.push({ id: topic.object.topicId, type: "topic" });
        }
      });
      props.onFilterChanged(props.columnDef.tableData.id, topics);
    },
    [topic]
  );
  return (
    <TaxonomySelector
      fullWidth
      multiSelect
      inputLabel={""}
      allowedSelections={["topic"]}
      filter={topic.length > 0 ? topic : null}
      onNodeSelected={(e) => {
        setTopic([...e]);
        console.log(e);
      }}
      noLabel
    />
  );
}

function DocumentIndexableWords(props) {
  const [wordsLoading, setWordsLoading] = useState(true);
  const [wordsError, setWordsError] = useState(false);
  const [words, setWords] = useState([]);

  useEffect(() => {
    getIndexableWords(props.documentID);
  }, []);

  const getIndexableWords = (documentId) => {
    axios
      .get(`/document/${documentId}/indexablewords`)
      .then((res) => {
        setWordsLoading(false);
        setWords(res.data);
      })
      .catch((err) => {
        setWordsLoading(false);
        setWordsError(true);
      });
  };

  return (
    <>
      {wordsLoading ? (
        <>
          <Skeleton width={150} height={20} animation="wave" />
          <Skeleton width={100} height={20} animation="wave" />
          <Skeleton width={120} height={20} animation="wave" />
          <Skeleton width={80} height={20} animation="wave" />
          <Skeleton width={150} height={20} animation="wave" />
          <Skeleton width={120} height={20} animation="wave" />
          <Skeleton width={150} height={20} animation="wave" />
        </>
      ) : (
        <>
          <Virtuoso
            totalCount={words.length}
            itemContent={(idx) => {
              return <code>{words[idx]}</code>;
            }}
          />
        </>
      )}
    </>
  );
}

function DocumentManager() {
  const [documentEdit, setDocumentEdit] = useState(null);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const location = useLocation();
  const documentTypes = useSelector((state) => state.prefs.documentTypes);
  const tableRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [cancelToken, setCancelToken] = useState(null)
  
  const sortLookup = {
    userTitle: "title",
    documentID: "documentID",
    date_created: "createdDate",
    language: "language",
  };

  useEffect(
    () => {
      setColumns([
        {
          title: t("global_topic"),
          field: "topicName",
          filterComponent: TaxonomyFilter,
          sorting: false,
        },
        {
          title: t("global_knowledge_source"),
          field: "knowledgeSourceId",
          sorting: false,
          defaultFilter:
            location.state && location.state.ksId ? location.state.ksId : null,
        },
        {
          title: t("document_title"),
          field: "userTitle",
        },
        {
          title: t("document_url"),
          field: "documentURL",
          sorting: false,
        },
        {
          title: t("global_language_dropdown"),
          field: "language",
          lookup: {
            en: t("language_en"),
            fr: t("language_fr"),
            de: t("language_de"),
            es: t("language_es"),
            it: t("language_it"),
            nl: t("language_nl"),
            ar: t("language_ar"),
            hi: t("language_hi"),
            pt: t("language_pt"),
            ru: t("language_ru"),
            th: t("language_th"),
          },
        },
        {
          title: t("required_reading_date_created"),
          field: "dateCreated",
          type: "datetime",
          filterPlaceholder: "Created after...",
          render: (rowData) => {
            if (!rowData.dateCreated) return null;
            return moment(rowData.dateCreated).format("DD/MM/YYYY");
          },
        },
        {
          title: t("document_status"),
          field: "status",
          sorting: false,
          defaultFilter: ["indexed", "errored"],
          lookup: {
            indexed: t("document_status_indexed"),
            errored: t("document_status_errored"),
          },
          render: (rowData) => {
            if (rowData.error) {
              return (
                <Tooltip
                  title={
                    <Typography>
                      {t("document_manager_document_error") +
                        " " +
                        rowData.error}
                    </Typography>
                  }
                >
                  <Error style={{ color: "red" }} />
                </Tooltip>
              );
            }
            return (
              <Tooltip
                title={
                  <Typography>
                    {t("document_status_indexed_tooltip")}
                  </Typography>
                }
              >
                <Check />
              </Tooltip>
            );
          },
        },
      ]);
    },
    [t,location]
  );

  const processFilters = (tableFilters) => {
    let filters = {};

    tableFilters.forEach((filter) => {
      switch (filter.column.field) {
        case "language":
          if (!!filter.value && filter.value.length > 0) {
            filters.language = filter.value;
          }
          break;
        case "topicName":
          if (!!filter.value && filter.value.length > 0) {
            filters.taxonomySelection = filter.value;
          }
          break;
        case "knowledgeSourceId":
          if (!!filter.value) {
            filters.knowledgeSourceId = filter.value;
          }
          break;
        case "status":
          if (filter.value.length === 1) {
            if (filter.value[0] === "indexed") {
              filters.includeErrors = false;
            } else if (filter.value[0] === "errored") {
              filters.onlyErrors = true;
            }
          }
          break;
        case "userTitle":
          if (!!filter.value) {
            filters.titleContains = filter.value;
          }
          break;
        case "dateCreated":
          if (!!filter.value) {
            filters.createdStart = filter.value.toISOString();
          }
          break;
        case "documentURL":
          if (!!filter.value) {
            filters.urlContains = filter.value;
          }
          break;
      }
    });

    return filters;
  };

  const deleteChip = (chip) => {
    let filtered = documentEdit.meta.filter((i) => i !== chip);
    setDocumentEdit({
      ...documentEdit,
      meta: [...filtered],
    });
  };

  const updateDocument = () => {
    axios
      .patch(`/document/${documentEdit.documentID}`, {
        ...documentEdit,
        dateCreated: null,
        dateModified: null,
        revalidationBehaviour: [
          "DO_NOTHING",
          "DISPLAY_WARNING",
          "HIDE_DOCUMENT",
          "NOT_SET",
        ][documentEdit.revalidationBehaviour],
        meta: JSON.stringify(documentEdit.meta),
      })
      .then((res) => {
        enqueueSnackbar(t("document_edited_successfully"), {
          variant: "success",
        });
        tableRef.current && tableRef.current.onQueryChange();
        setDocumentEdit(null);
      })
      .catch((err) => {
        enqueueSnackbar(t("document_edited_error_occurred"), {
          variant: "error",
        });
      });
  };

  return (
    <div
      style={{
        overflow: "auto",
        padding: 10,
        height: "100%",
        backgroundColor: "#eae0d7",
        backgroundImage: "url('../images/background.svg')",
      }}
    >
      <MaterialTable
        isLoading={loading}
        onRowClick={(_event, _rowData, togglePanel) => togglePanel()}
        columns={columns}
        tableRef={tableRef}
        data={(query) =>
          new Promise((resolve, reject) => {
            if (cancelToken && cancelToken.cancel) {
              // cancel inflight data request
              cancelToken.cancel();
            }
            const CancelToken = axiosImport.CancelToken;
            const source = CancelToken.source();
            setCancelToken(source);
            axios
              .post("/document/", {
                page: query.page,
                size: query.pageSize,
                sort: query.orderBy ? sortLookup[query.orderBy.field] : "documentID",
                sortDirection: query.orderDirection,
                includeErrors: true,
                ...processFilters(query.filters),
              },{ cancelToken: source.token })
              .then((res) => {
                resolve({
                  data: res.data.content,
                  page: res.data.number,
                  totalCount: res.data.totalElements,
                });
                setLoading(false);
              })
              .catch((err) => {
                reject(err);
              });
          })          
        }
        actions={[
          (rowData) => ({
            icon: "edit",
            tooltip: t("edit_document"),
            onClick: (e, rowData) => {
              e.preventDefault();
              e.stopPropagation();
              setDocumentEdit({
                ...rowData,
                meta: rowData.meta ? JSON.parse(rowData.meta) : [],
                dateVisibleFrom: !!rowData.dateVisibleFrom
                  ? moment(rowData.dateVisibleFrom)
                  : null,
                dateVisibleTo: !!rowData.dateVisibleTo
                  ? moment(rowData.dateVisibleTo)
                  : null,
                revalidationDate: !!rowData.revalidationDate
                  ? moment(rowData.revalidationDate)
                  : null,
              });
            },
          }),
        ]}
        options={{
          pageSizeOptions: [5, 10, 15, 20],
          filtering: true,
          headerStyle: { position: "sticky", top: 0 },
          actionsColumnIndex: -1,
          draggable: false,
          pageSize: 10,
          // debounceInterval: 500,
          padding: "dense",
          search: false,
        }}
        title={t("global_document_plural")}
        detailPanel={(rowData) => {
          return (
            <div className={classes.detailContainer}>
              <TableContainer
                component={Paper}
                className={classes.settingsTable}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("document_info")}</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowData.rowData.error ? (
                      <TableRow>
                        <TableCell component="th" scope="row">
                          {t("document_error_table_cell")}
                        </TableCell>
                        <TableCell align="right" style={{ display: "flex" }}>
                          <Typography variant="body2" style={{ marginTop: 2 }}>
                            {rowData.rowData.error}
                          </Typography>
                          <Error style={{ color: "red", marginRight: 5 }} />
                        </TableCell>
                      </TableRow>
                    ) : (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {t("document_id")}
                      </TableCell>
                      <TableCell align="right">
                          <Link
                            to={`/full/document/${rowData.rowData.documentID}`}
                            className="document-link"
                          >
                            {rowData.rowData.documentID}
                          </Link>
                      </TableCell>
                    </TableRow>
                    )}
                    {(rowData.rowData.itemId && rowData.rowData.itemId !== null) && (
                      <TableRow>
                        <TableCell component="th" scope="row">
                          {t("global_knowledge_item")}
                        </TableCell>
                        <TableCell align="right">
                          <Link
                            to={`/full/knowledgeitem/${rowData.rowData.itemId}`}
                            className="document-link"
                          >
                            {rowData.rowData.itemId}
                          </Link>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {t("document_content_type")}
                      </TableCell>
                      <TableCell align="right">
                        {rowData.rowData.contentType}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {t("document_type")}
                      </TableCell>
                      <TableCell align="right">
                        {documentTypes
                          .filter(
                            (type) =>
                              type.documentTypeID ===
                              rowData.rowData.documentTypeId
                          )
                          .map((type) => type.name)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {t("settings_document_number_of_times_used")}
                      </TableCell>
                      <TableCell align="right">
                        {rowData.rowData.usedCount}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {t("settings_document_number_of_times_viewed")}
                      </TableCell>
                      <TableCell align="right">
                        {rowData.rowData.viewedCount}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Card className={classes.indexableWords}>
                <CardContent
                  style={{ height: "88%", display: "flex", flexFlow: "column" }}
                >
                  <Typography variant="h6" gutterBottom>
                    {t("document_indexed_terms")}
                  </Typography>
                  <div style={{ flex: 1 }}>
                    <DocumentIndexableWords
                      documentID={rowData.rowData.documentID}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        }}
      />
      {!!documentEdit && (
        <Dialog open={true} onClose={() => setDocumentEdit(null)} fullWidth>
          <DialogTitle>{t("edit_document")}</DialogTitle>
          {documentEdit.isKnowledgeItem && (
            <Alert severity="info">{t("document_manager_ki_info")}</Alert>
          )}
          <DialogContent style={{ display: "flex", flexDirection: "column" }}>
            <TextField
              label={t("document_title")}
              value={documentEdit.userTitle}
              id="document-title"
              onChange={(e) =>
                setDocumentEdit({ ...documentEdit, userTitle: e.target.value })
              }
              style={{ marginBottom: 5 }}
              disabled={documentEdit.isKnowledgeItem}
            />
            <FormControl className={classes.docLanguage}>
              <InputLabel htmlFor="doc-language">
                {t("document_language")}
              </InputLabel>
              <Select
                id="doc-language"
                value={documentEdit.language}
                onChange={(event) =>
                  setDocumentEdit({
                    ...documentEdit,
                    language: event.target.value,
                  })
                }
              >
                {languages.map((l) => {
                  return <MenuItem value={l}>{t(`language_${l}`)}</MenuItem>;
                })}
              </Select>
              <FormHelperText>
                {t("document_language_helper_text")}
              </FormHelperText>
            </FormControl>
            <DocumentType
              doctypes={documentTypes}
              docTypeSelected={(e) =>
                setDocumentEdit({
                  ...documentEdit,
                  documentTypeId: e.target.value,
                })
              }
              documentType={[
                documentEdit.documentTypeId ? documentEdit.documentTypeId : -1,
              ]}
              disabled={documentEdit.isKnowledgeItem}
            />
            {!documentEdit.isKnowledgeItem && (
              <DocumentCategories
                style={{ marginTop: 10 }}
                onChange={(categories) =>
                  setDocumentEdit({
                    ...documentEdit,
                    documentCategories: categories.map((c) => c.id),
                  })
                }
                defaultValue={documentEdit.documentCategories}
              />
            )}
            <Typography style={{ marginTop: 15 }} gutterBottom>
              {t("content_setup_visibility_date_range")}
            </Typography>
            <div style={{ display: "flex", flex: 1 }}>
              <KeyboardDatePicker
                disabled={documentEdit.isKnowledgeItem}
                format="DD/MM/YYYY"
                autoOk
                label={t("content_setup_visible_from")}
                style={{ marginRight: 10 }}
                clearable
                id="visible-from"
                value={documentEdit.dateVisibleFrom}
                onChange={(date) =>
                  setDocumentEdit({
                    ...documentEdit,
                    dateVisibleFrom: date,
                  })
                }
              />
              <KeyboardDatePicker
                disabled={documentEdit.isKnowledgeItem}
                format="DD/MM/YYYY"
                autoOk
                label={t("content_setup_visible_to")}
                clearable
                id="visible-to"
                value={documentEdit.dateVisibleTo}
                onChange={(date) =>
                  setDocumentEdit({
                    ...documentEdit,
                    dateVisibleTo: date,
                  })
                }
              />
            </div>
            <Typography style={{ marginTop: 15 }} gutterBottom>
              {t("global_revalidation")}
            </Typography>
            <FormControl className={classes.formControl}>
              <InputLabel htmlFor="revalBehaviour">
                {t("content_setup_revalidation_behaviour")}
              </InputLabel>
              <Select
                disabled={documentEdit.isKnowledgeItem}
                id="revalidation-behaviour"
                value={documentEdit.revalidationBehaviour}
                onChange={(event) =>
                  setDocumentEdit({
                    ...documentEdit,
                    revalidationBehaviour: event.target.value,
                  })
                }
              >
                <MenuItem value={0}>{t("content_setup_do_nothing")}</MenuItem>
                <MenuItem value={1}>
                  {t("content_setup_display_a_warning")}
                </MenuItem>
                <MenuItem value={2}>
                  {t("content_setup_hide_document")}
                </MenuItem>
                <MenuItem value={3}>{t("content_setup_none_set")}</MenuItem>
              </Select>
            </FormControl>
            <KeyboardDatePicker
              disabled={documentEdit.isKnowledgeItem}
              format="DD/MM/YYYY"
              autoOk
              label={t("content_setup_revalidation_date")}
              id="revalidation-date"
              clearable
              value={documentEdit.revalidationDate}
              onChange={(date) =>
                setDocumentEdit({
                  ...documentEdit,
                  revalidationDate: date,
                })
              }
            />
            {!documentEdit.isKnowledgeItem && (
              <ChipInput
                fullWidthInput
                value={documentEdit.meta}
                onAdd={(chip) =>
                  setDocumentEdit({
                    ...documentEdit,
                    meta: [...documentEdit.meta, chip],
                  })
                }
                onDelete={deleteChip}
                label={t("content_meta_label")}
                helperText={t("edit_document_metadata")}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDocumentEdit(null)}>
              {t("global_cancel")}
            </Button>
            <Button
              onClick={() => updateDocument()}
              id="save-document"
              disabled={!documentEdit.userTitle || documentEdit.isKnowledgeItem}
            >
              {t("global_save")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

export default DocumentManager;
